import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useParams } from 'react-router-dom';
import API_URL from '../api/baseURL'; // adjust the path as needed!
import VinInput, { isVinFormatValid } from '../components/VinInput';
import CompanySelect, { TECH_CHECK_COMPANIES } from '../components/CompanySelect';
import VehicleTelematicsPanel from '../components/vehicle/telematics/VehicleTelematicsPanel';

const VehicleDetail = () => {
  const { token, user } = useAuth();
  const { id: vehicleId } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transfer, setTransfer] = useState({ accountId: '', email: '', phone: '' });
  const [transferResult, setTransferResult] = useState(null);
  const [error, setError] = useState('');

  // NEW STATE FOR EDITING
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    vin: '',
    insuranceCompany: '',
    insuranceExpiry: '',
    technicalCompany: '',
    technicalExpiry: ''
  });
  const [editError, setEditError] = useState('');

  const getOwnerId = v =>
    v && v.owner
      ? typeof v.owner === "object"
        ? v.owner._id
        : v.owner
      : undefined;

  const ownerId = getOwnerId(vehicle);
  const isCurrentOwner = user && ownerId && (ownerId === user.id || ownerId === user._id);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setVehicle(res.data);
        setLoading(false);
      })
      .catch(() => {
        setVehicle(null);
        setLoading(false);
      });
  }, [vehicleId, token]);

  const handleTransferChange = e => {
    setTransfer({ ...transfer, [e.target.name]: e.target.value });
  };

  const handleTransferSubmit = async e => {
    e.preventDefault();
    setError('');
    setTransferResult(null);
    try {
      const res = await axios.post(`${API_URL}/vehicles/${vehicleId}/transfer`,
        transfer,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTransferResult(res.data);
      setVehicle(v => v ? { ...v, owner: res.data.newOwner.id } : v);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Transfer failed'
      );
    }
  };

  // HANDLE EDITING INPUTS
  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError('');

    // Validate VIN if provided
    if (editData.vin && !isVinFormatValid(editData.vin)) {
      setEditError('VIN must be exactly 17 characters with valid format.');
      return;
    }

    try {
      const updated = {
        vin: editData.vin,
        insurance: {
          company: editData.insuranceCompany,
          expiry: editData.insuranceExpiry
        },
        technicalCheck: {
          company: editData.technicalCompany,
          expiry: editData.technicalExpiry
        }
      };
      const res = await axios.patch(`${API_URL}/vehicles/${vehicleId}`, updated, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicle(res.data);
      setEditMode(false);
    } catch (err) {
      console.error('Update failed', err);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!vehicle) return <div className="p-6 text-red-500">Vehicle not found.</div>;

  return (
    <div className="max-w-xl mx-auto p-6 bg-[#17140F] dark:bg-[#100F0D] rounded shadow mt-8">
      <h2 className="text-2xl font-bold mb-4 text-blue-700 dark:text-blue-200">Vehicle Details</h2>
      {isCurrentOwner && <VehicleTelematicsPanel vehicleId={vehicleId} />}
      <table className="table-auto w-full mb-6 text-[#F4F1EA] text-[#F4F1EA]">
        <tbody>
          <tr>
            <td className="font-semibold py-1">Plate Number:</td>
            <td>{vehicle.plateNumber}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Brand:</td>
            <td>{vehicle.brand}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Model:</td>
            <td>{vehicle.model}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Year:</td>
            <td>{vehicle.year}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">VIN:</td>
            <td>{vehicle.vin}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Current Owner ID:</td>
            <td>{typeof vehicle.owner === "object" ? vehicle.owner._id : vehicle.owner}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Owner Email:</td>
            <td>{typeof vehicle.owner === "object" ? vehicle.owner.email : "—"}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Owner Phone:</td>
            <td>{typeof vehicle.owner === "object" ? (vehicle.owner.details?.phone || "—") : "—"}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Insurance Company:</td>
            <td>{vehicle.insurance?.company || "—"}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Insurance Expiry:</td>
            <td>{vehicle.insurance?.expiry ? new Date(vehicle.insurance.expiry).toLocaleDateString() : "—"}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Technical Check Company:</td>
            <td>{vehicle.technicalCheck?.company || "—"}</td>
          </tr>
          <tr>
            <td className="font-semibold py-1">Technical Check Expiry:</td>
            <td>{vehicle.technicalCheck?.expiry ? new Date(vehicle.technicalCheck.expiry).toLocaleDateString() : "—"}</td>
          </tr>
        </tbody>
      </table>

      {isCurrentOwner && (
        <>
          <button onClick={() => {
            setEditData({
              vin: vehicle.vin || '',
              insuranceCompany: vehicle.insurance?.company || '',
              insuranceExpiry: vehicle.insurance?.expiry?.split('T')[0] || '',
              technicalCompany: vehicle.technicalCheck?.company || '',
              technicalExpiry: vehicle.technicalCheck?.expiry?.split('T')[0] || ''
            });
            setEditError('');
            setEditMode(true);
          }} className="bg-yellow-500 text-white px-4 py-2 rounded mb-4">Edit Details</button>

          {editMode && (
            <form onSubmit={handleEditSubmit} className="space-y-3 border p-4 rounded mb-4 bg-[#1B1815] dark:bg-[#17140F]">
              <div>
                <label className="block text-sm font-medium mb-1 text-[#C9C3B6] dark:text-gray-300">VIN (Vehicle Identification Number)</label>
                <VinInput value={editData.vin} onChange={handleEditChange} required />
              </div>
              {/* Free text for now — switch back to a dropdown once the full list
                  of market insurers is gathered. */}
              <input
                type="text"
                name="insuranceCompany"
                value={editData.insuranceCompany}
                onChange={handleEditChange}
                className="w-full border px-3 py-2 rounded"
                placeholder="Compagnie d'assurance"
              />
              <input type="date" name="insuranceExpiry" value={editData.insuranceExpiry} onChange={handleEditChange} className="w-full border px-3 py-2 rounded" />
              <CompanySelect
                value={editData.technicalCompany}
                onChange={(v) => setEditData(prev => ({ ...prev, technicalCompany: v }))}
                options={TECH_CHECK_COMPANIES}
                className="w-full border px-3 py-2 rounded"
                placeholder="Centre de contrôle technique"
              />
              <input type="date" name="technicalExpiry" value={editData.technicalExpiry} onChange={handleEditChange} className="w-full border px-3 py-2 rounded" />
              {editError && <p className="text-red-600 text-sm">{editError}</p>}
              <div className="flex gap-2">
                <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded">Save</button>
                <button type="button" onClick={() => setEditMode(false)} className="border border-[#2B2724] text-[#C9C3B6] hover:text-[#F4F1EA] px-4 py-2 rounded-[9px] text-[13px]">Annuler</button>
              </div>
            </form>
          )}
        </>
      )}

      {isCurrentOwner && (
        <div className="mt-6 p-4 border rounded bg-blue-50 dark:bg-[#17140F]">
          <h3 className="font-semibold mb-2 text-blue-600 dark:text-blue-300">Transfer Ownership</h3>
          <p className="text-xs text-[#8F877B] mb-2">Enter the account ID, email, and phone number of the new owner to ensure secure transfer.</p>
          <form onSubmit={handleTransferSubmit} className="space-y-2">
            <input type="text" name="accountId" placeholder="New Owner Account ID" value={transfer.accountId} onChange={handleTransferChange} className="w-full border px-3 py-2 rounded" required />
            <input type="email" name="email" placeholder="New Owner Email" value={transfer.email} onChange={handleTransferChange} className="w-full border px-3 py-2 rounded" required />
            <input type="text" name="phone" placeholder="New Owner Phone" value={transfer.phone} onChange={handleTransferChange} className="w-full border px-3 py-2 rounded" required />
            <button type="submit" className="bg-blue-600 text-white rounded px-4 py-2">Transfer</button>
          </form>
          {error && <div className="text-red-500 mt-2">{error}</div>}
          {transferResult && <div className="text-green-600 mt-2">Ownership transferred successfully!</div>}
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;
